import { useState } from "react";
import Link from "@/components/ui/RouterLink";
import { ArrowRight, RotateCcw } from "lucide-react";
import { SectionEyebrow } from "@/components/ui/SectionEyebrow";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

type Answers = {
  hosting?: string;
  control?: string;
  priority?: string;
};

const questions = [
  {
    key: "hosting" as const,
    label: "What are you hosting?",
    options: [
      "Website",
      "Web application",
      "Ecommerce platform",
      "Database",
      "Internal business system",
      "Email",
      "Custom workload",
    ],
  },
  {
    key: "control" as const,
    label: "How much control do you need?",
    options: ["Basic", "Advanced", "Full server control", "Not sure"],
  },
  {
    key: "priority" as const,
    label: "What matters most?",
    options: ["Lower cost", "Performance", "Scalability", "Security", "Custom configuration"],
  },
];

function recommend(answers: Answers): { label: string; href: string } {
  if (answers.hosting === "Email") return { label: "Business Email", href: "/email-solutions" };
  if (answers.priority === "Security") return { label: "Security & Network", href: "/security-network" };
  if (answers.control === "Full server control")
    return { label: "Dedicated Server", href: "/dedicated-servers" };
  if (answers.priority === "Scalability" || answers.hosting === "Custom workload")
    return { label: "Cloud Solution", href: "/cloud-solutions" };
  if (answers.control === "Not sure" || answers.hosting === "Internal business system")
    return { label: "Custom Consultation", href: "/contact" };
  return { label: "VPS Hosting", href: "/vps-hosting" };
}

export function SolutionSelector() {
  const [answers, setAnswers] = useState<Answers>({});
  const [step, setStep] = useState(0);

  const done = step >= questions.length;
  const currentQuestion = questions[Math.min(step, questions.length - 1)]!;
  const result = done ? recommend(answers) : null;

  function choose(key: keyof Answers, value: string) {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step + 1 === questions.length) {
      analytics.track("solution_selector_complete", next as Record<string, string>);
    }
    setStep((s) => s + 1);
  }

  function reset() {
    setAnswers({});
    setStep(0);
  }

  return (
    <section id="solutions" className="mx-auto max-w-4xl px-5 py-20 lg:px-8">
      <div className="text-center">
        <SectionEyebrow>Guided Selection</SectionEyebrow>
        <h2 className="mt-5 font-display text-3xl font-semibold text-text-primary sm:text-4xl">
          Not Sure Which Solution You Need?
        </h2>
      </div>

      <div className="glass-panel mt-10 rounded-card p-6 sm:p-10">
        {!done && (
          <>
            <div className="mb-6 flex items-center gap-2">
              {questions.map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    "h-1 flex-1 rounded-full",
                    i < step ? "bg-cyan" : i === step ? "bg-cyan/50" : "bg-border"
                  )}
                />
              ))}
            </div>
            <p className="text-sm font-medium uppercase tracking-wide text-text-muted">
              Question {step + 1} of {questions.length}
            </p>
            <h3 className="mt-2 font-display text-xl font-semibold text-text-primary sm:text-2xl">
              {currentQuestion.label}
            </h3>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => choose(currentQuestion.key, opt)}
                  className="focus-ring rounded-lg border border-border bg-navy-card px-4 py-3.5 text-left text-sm font-medium text-text-primary transition hover:border-cyan/50 hover:bg-navy-secondary"
                >
                  {opt}
                </button>
              ))}
            </div>
          </>
        )}

        {done && result && (
          <div className="text-center">
            <p className="text-sm font-medium uppercase tracking-wide text-cyan">Recommended</p>
            <h3 className="mt-2 font-display text-2xl font-semibold text-text-primary sm:text-3xl">
              {result.label}
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm text-text-secondary">
              Based on your answers, this is a practical starting point. You can always talk to
              our team to confirm the right fit before purchasing.
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                href={result.href}
                className="focus-ring inline-flex items-center gap-2 rounded-full bg-gradient-brand px-6 py-3 text-sm font-medium text-white transition hover:brightness-110"
              >
                View Recommended Solution <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={reset}
                className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-3 text-sm font-medium text-text-secondary transition hover:text-text-primary"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
